import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { GeneralFilterSchema } from '../../../models/dashboard/filter-forms/general-filter.model';
import CustomSelectField from '../../FormCom/CustomSelectField';
import { FormComponent } from '../../FormCom/FormComponent';
import OptionalFormField from '../../FormCom/OptionalFormField';

// 'breakdown': breakdown selector + year, month shown only when breakdown is 'Monthly'
// 'year': a single year selector
// 'yearMonth': year + month selectors, both always visible
export type ChartFilterVariant = 'breakdown' | 'year' | 'yearMonth';

type ChartFilterFormValues = {
  breakdown?: string;
  year: string;
  month?: string;
};

const monthOptions = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const monthNameToNumber: Record<string, string> = {
  january: '01',
  february: '02',
  march: '03',
  april: '04',
  may: '05',
  june: '06',
  july: '07',
  august: '08',
  september: '09',
  october: '10',
  november: '11',
  december: '12',
};

const now = new Date();

const buildFormInitialState = (
  variant: ChartFilterVariant,
): ChartFilterFormValues => ({
  ...(variant === 'breakdown' ? { breakdown: 'Monthly' } : {}),
  year: now.getFullYear().toString(),
  month: monthOptions[now.getMonth()],
});

interface ChartFilterProps {
  setDataUrl: (url: string) => void;
  dataUrl: string;
  variant?: ChartFilterVariant;
}

const ChartFilter: React.FC<ChartFilterProps> = ({
  setDataUrl,
  dataUrl,
  variant = 'breakdown',
}) => {
  const baseDataUrlRef = useRef(dataUrl);
  const [formInitialState] = useState(() => buildFormInitialState(variant));
  const [filterObj, setFilterObj] =
    useState<Partial<ChartFilterFormValues>>(formInitialState);

  useEffect(() => {
    const baseUrl = baseDataUrlRef.current;

    const selectedYear = String(filterObj.year ?? '').trim();
    const selectedMonthName = String(filterObj.month ?? '')
      .trim()
      .toLowerCase();
    const selectedMonthNumber = monthNameToNumber[selectedMonthName];

    let queryString = '';

    if (variant === 'breakdown') {
      const selectedBreakdownBy = String(filterObj.breakdown ?? '').trim();
      const queryParts = [];

      if (selectedBreakdownBy) {
        queryParts.push(`breakdown=${encodeURIComponent(selectedBreakdownBy)}`);
      }

      if (selectedYear && selectedMonthNumber) {
        queryParts.push(
          `month=${encodeURIComponent(`${selectedYear}-${selectedMonthNumber}`)}`,
        );
      }

      queryString = queryParts.join('&');
    } else if (variant === 'year') {
      queryString = selectedYear
        ? `year=${encodeURIComponent(`${selectedYear}`)}`
        : '';
    } else {
      queryString =
        selectedYear && selectedMonthNumber
          ? `month=${encodeURIComponent(`${selectedYear}-${selectedMonthNumber}`)}`
          : '';
    }

    if (!queryString) {
      setDataUrl(baseUrl);
      return;
    }

    if (baseUrl.includes('?')) {
      const separator =
        baseUrl.endsWith('?') || baseUrl.endsWith('&') ? '' : '&';
      setDataUrl(`${baseUrl}${separator}${queryString}`);
      return;
    }

    setDataUrl(`${baseUrl}?${queryString}`);
  }, [filterObj, setDataUrl, variant]);

  const handleOnSubmit = () => {};

  const handleFieldChange = (fieldName: keyof ChartFilterFormValues) => {
    return (value: string) => {
      setFilterObj((prevFilter) => ({
        ...prevFilter,
        [fieldName]: value,
      }));
    };
  };

  if (variant === 'year') {
    return (
      <SimpleContainer>
        <YearOnlyFormComponent
          initialValues={formInitialState}
          schema={GeneralFilterSchema}
          onSubmit={handleOnSubmit}
        >
          <CustomSelectField
            name='year'
            id='year'
            placeholder='Year'
            onChange={handleFieldChange('year')}
            optionData={Array.from({ length: 77 }, (_, i) =>
              (2024 + i).toString(),
            )}
          />
        </YearOnlyFormComponent>
      </SimpleContainer>
    );
  }

  if (variant === 'yearMonth') {
    return (
      <SimpleContainer>
        <YearMonthFormComponent
          initialValues={formInitialState}
          schema={GeneralFilterSchema}
          onSubmit={handleOnSubmit}
        >
          <CustomSelectField
            name='year'
            id='year'
            placeholder='Year'
            onChange={handleFieldChange('year')}
            optionData={Array.from({ length: 77 }, (_, i) =>
              (2024 + i).toString(),
            )}
          />
          <CustomSelectField
            name='month'
            id='month'
            placeholder='Month'
            onChange={handleFieldChange('month')}
            optionData={monthOptions}
          />
        </YearMonthFormComponent>
      </SimpleContainer>
    );
  }

  return (
    <Container>
      <WrapFormComponent
        initialValues={formInitialState}
        schema={GeneralFilterSchema}
        onSubmit={handleOnSubmit}
      >
        <BreakdownContainer>
          <CustomSelectField
            name='breakdown'
            id='breakdown'
            placeholder='day by day'
            onChange={handleFieldChange('breakdown')}
            optionData={['Day by day', 'Weekly', 'Monthly']}
          />
          <VerticalDivider></VerticalDivider>
        </BreakdownContainer>

        <YearField
          name='year'
          id='year'
          placeholder='Year'
          onChange={handleFieldChange('year')}
          optionData={Array.from({ length: 77 }, (_, i) =>
            (2026 + i).toString(),
          )}
        />

        <OptionalFormField
          conditionKey='breakdown'
          conditionValue={'Monthly'}
          resetField='month'
          negate
        >
          <MonthField
            name='month'
            id='month'
            placeholder='Month'
            onChange={handleFieldChange('month')}
            optionData={monthOptions}
          />
        </OptionalFormField>
      </WrapFormComponent>
    </Container>
  );
};

export default ChartFilter;

const Container = styled.div`
  @media (max-width: 884px) {
    width: 100%;
  }
`;

const SimpleContainer = styled.div``;

const WrapFormComponent = styled(FormComponent)`
  display: flex;
  gap: 8px;

  & > * {
    flex: 0 0 auto;
  }

  @media (max-width: 480px) {
    flex-wrap: wrap;

    & > * {
      flex: 1 1 calc(50% - 4px);
    }
  }
`;

const YearOnlyFormComponent = styled(FormComponent)`
  display: grid;
  grid-template-columns: 109px;
  gap: 8px;
`;

const YearMonthFormComponent = styled(FormComponent)`
  display: grid;
  grid-template-columns: 109px 133px;
  gap: 8px;
`;

const YearField = styled(CustomSelectField)`
  width: 109px;
`;

const MonthField = styled(CustomSelectField)`
  width: 133px;
`;

const BreakdownContainer = styled.div`
  display: grid;
  grid-template-columns: 123px 1px;
  gap: 8px;
  align-items: center;
`;

const VerticalDivider = styled.div`
  width: 1px;
  height: 20px;
  background-color: ${({ theme }) => theme.palette.borderColor};
`;
